/**
 * Analytics Page
 * Platform-wide marketing & product analytics for internal Tilly team
 * Displays visitor analytics, experiment funnels, and raw events across all landing pages
 */

import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  MenuItem,
  Select,
} from '@mui/material';
import { Refresh as RefreshIcon, Download as DownloadIcon } from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { adminAnalyticsApi } from '@/api/client';
import type {
  GetAnalyticsSummaryResponse,
  GetAnalyticsEventsResponse,
  GetExperimentFunnelResponse,
  AnalyticsTrendPointDTO,
  AnalyticsTopPageDTO,
  AnalyticsTopEventDTO,
  AnalyticsReferrerRowDTO,
  AnalyticsGeoRowDTO,
} from '@tilly/shared/types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;
  return (
    <div hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Helper: format milliseconds to human-readable duration
function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

// Helper: format date to YYYY-MM-DD
function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Helper: parse YYYY-MM-DD to Date
function parseDate(dateStr: string): Date {
  return new Date(dateStr + 'T00:00:00Z');
}

export function AnalyticsPage() {
  const [tabValue, setTabValue] = useState(0);

  // Shared state across tabs
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDateForInput(d);
  });
  const [toDate, setToDate] = useState(() => formatDateForInput(new Date()));
  const [pageFilter, setPageFilter] = useState('');
  const [experimentIdFilter, setExperimentIdFilter] = useState('');

  // Overview tab state
  const [overviewData, setOverviewData] = useState<Partial<
    GetAnalyticsSummaryResponse['data']
  > | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  // Experiments tab state
  const [experimentFunnelData, setExperimentFunnelData] = useState<Partial<
    GetExperimentFunnelResponse['data']
  > | null>(null);
  const [experimentLoading, setExperimentLoading] = useState(false);
  const [experimentError, setExperimentError] = useState<string | null>(null);

  // Raw Events tab state
  const [eventsData, setEventsData] = useState<Partial<GetAnalyticsEventsResponse['data']> | null>(
    null
  );
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventsCursor, setEventsCursor] = useState<string | null>(null);
  const [eventsEventName, setEventsEventName] = useState('');
  const [eventsPage, setEventsPage] = useState('');

  // Load overview data
  const loadOverview = async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const data = await adminAnalyticsApi.getSummary({
        from: fromDate,
        to: toDate,
        page: pageFilter || undefined,
        experiment_id: experimentIdFilter || undefined,
      });
      console.log('[Analytics] Overview response:', data);
      // API client unwraps the response structure, so we get the data directly
      setOverviewData(data as any);
    } catch (err) {
      console.error('[Analytics] Overview error:', err);
      setOverviewError(err instanceof Error ? err.message : 'Failed to load overview');
    } finally {
      setOverviewLoading(false);
    }
  };

  // Load experiment funnel
  const loadExperimentFunnel = async () => {
    if (!experimentIdFilter.trim()) {
      setExperimentError('Please enter an Experiment ID');
      return;
    }
    setExperimentLoading(true);
    setExperimentError(null);
    try {
      const data = await adminAnalyticsApi.getExperimentFunnel(experimentIdFilter);
      setExperimentFunnelData(data as any);
    } catch (err) {
      setExperimentError(err instanceof Error ? err.message : 'Failed to load experiment funnel');
    } finally {
      setExperimentLoading(false);
    }
  };

  // Load events
  const loadEvents = async (cursor?: string | null) => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      const rawData = await adminAnalyticsApi.getEvents({
        from: fromDate,
        to: toDate,
        event_name: eventsEventName || undefined,
        limit: 50,
        cursor: cursor ?? undefined,
      });
      // API client unwraps the response, so we get the inner structure
      const data = rawData as any;
      if (cursor) {
        // Append to existing
        setEventsData({
          events: [...(eventsData?.events ?? []), ...(data.events ?? [])],
          next_cursor: data.next_cursor,
        } as any);
      } else {
        setEventsData(data);
      }
      setEventsCursor((data.next_cursor ?? null) as any);
    } catch (err) {
      setEventsError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  };

  // Export events as CSV
  const exportEventsAsCSV = async () => {
    if (!fromDate || !toDate) {
      setEventsError('Please set date range first');
      return;
    }
    setEventsLoading(true);
    setEventsError(null);
    try {
      const blob = await adminAnalyticsApi.exportEventsAsCSV({
        from: fromDate,
        to: toDate,
        event_name: eventsEventName || undefined,
      });
      // Create a temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `events-${fromDate}-to-${toDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setEventsError(err instanceof Error ? err.message : 'Failed to export events');
    } finally {
      setEventsLoading(false);
    }
  };

  // Load overview on mount and when filters change
  useEffect(() => {
    if (tabValue === 0) {
      loadOverview();
    }
  }, [tabValue]);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4">Analytics</Typography>
        </Box>

        {/* Shared filters */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Date Range & Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Page Path (optional)"
                value={pageFilter}
                onChange={(e) => setPageFilter(e.target.value)}
                placeholder="/lp/salon-pain"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Experiment ID (optional)"
                value={experimentIdFilter}
                onChange={(e) => setExperimentIdFilter(e.target.value)}
                placeholder="salon-pain"
                fullWidth
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => loadOverview()}>
              Apply
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() - 7);
                setFromDate(formatDateForInput(d));
                setToDate(formatDateForInput(new Date()));
              }}
            >
              Last 7 days
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() - 30);
                setFromDate(formatDateForInput(d));
                setToDate(formatDateForInput(new Date()));
              }}
            >
              Last 30 days
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() - 90);
                setFromDate(formatDateForInput(d));
                setToDate(formatDateForInput(new Date()));
              }}
            >
              Last 90 days
            </Button>
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Overview" />
            <Tab label="Experiments" />
            <Tab label="Raw Events" />
          </Tabs>

          {/* ─── Overview Tab ─────────────────────────────────────────── */}
          <TabPanel value={tabValue} index={0}>
            {overviewError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {overviewError}
              </Alert>
            )}

            {overviewLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!overviewLoading && overviewData && overviewData.kpis && (
              <Box>
                {/* KPI Cards */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {[
                    { label: 'Page Views', value: overviewData.kpis.page_views },
                    { label: 'Unique Visitors', value: overviewData.kpis.unique_visitors },
                    { label: 'Unique Sessions', value: overviewData.kpis.unique_sessions },
                    {
                      label: 'Avg Engagement',
                      value: formatDuration(overviewData.kpis.avg_engagement_ms),
                    },
                    {
                      label: 'Bounce Rate',
                      value: `${(overviewData.kpis.bounce_rate * 100).toFixed(1)}%`,
                    },
                  ].map((kpi) => (
                    <Grid item xs={12} sm={6} md={2.4} key={kpi.label}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            {kpi.label}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {kpi.value}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Trend Chart */}
                {overviewData.trend && overviewData.trend.length > 0 && (
                  <Paper sx={{ p: 2, mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Daily Trend
                    </Typography>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart
                        data={overviewData.trend}
                        margin={{ top: 4, right: 16, left: -16, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="page_views"
                          stroke="#7FA897"
                          isAnimationActive={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="unique_visitors"
                          stroke="#4A90A4"
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Paper>
                )}

                {/* Top Pages */}
                {overviewData.top_pages && overviewData.top_pages.length > 0 && (
                  <Paper sx={{ p: 2, mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Top Pages
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Views
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Unique Visitors
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Avg Engagement
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Bounce Rate
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overviewData.top_pages.map((row) => (
                            <TableRow key={row.page}>
                              <TableCell>{row.page}</TableCell>
                              <TableCell align="right">{row.page_views}</TableCell>
                              <TableCell align="right">{row.unique_visitors}</TableCell>
                              <TableCell align="right">
                                {formatDuration(row.avg_engagement_ms)}
                              </TableCell>
                              <TableCell align="right">
                                {(row.bounce_rate * 100).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Top Events */}
                {overviewData.top_events && overviewData.top_events.length > 0 && (
                  <Paper sx={{ p: 2, mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Top Events
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Count
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Unique Visitors
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overviewData.top_events.map((row) => (
                            <TableRow key={row.event_name}>
                              <TableCell>{row.event_name}</TableCell>
                              <TableCell align="right">{row.count}</TableCell>
                              <TableCell align="right">{row.unique_visitors}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Referrer Breakdown */}
                {overviewData.referrer_breakdown && overviewData.referrer_breakdown.length > 0 && (
                  <Paper sx={{ p: 2, mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Traffic Sources
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Referrer</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Views
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Visitors
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              %
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overviewData.referrer_breakdown.map((row, idx) => (
                            <TableRow key={`${row.referrer_type}-${idx}`}>
                              <TableCell>{row.referrer_type}</TableCell>
                              <TableCell>{row.referrer || '(direct)'}</TableCell>
                              <TableCell align="right">{row.page_views}</TableCell>
                              <TableCell align="right">{row.unique_visitors}</TableCell>
                              <TableCell align="right">
                                {(row.percentage * 100).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Geo Breakdown */}
                {overviewData.geo_breakdown && overviewData.geo_breakdown.length > 0 && (
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Geography
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Views
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Visitors
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              %
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overviewData.geo_breakdown.slice(0, 20).map((row, idx) => (
                            <TableRow key={`${row.country}-${idx}`}>
                              <TableCell>{row.country}</TableCell>
                              <TableCell>{row.region || '-'}</TableCell>
                              <TableCell align="right">{row.page_views}</TableCell>
                              <TableCell align="right">{row.unique_visitors}</TableCell>
                              <TableCell align="right">
                                {(row.percentage * 100).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Box>
            )}
          </TabPanel>

          {/* ─── Experiments Tab ────────────────────────────────────── */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Enter an experiment ID to view its funnel breakdown by variant. Uses the date range
                above.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Experiment ID"
                  value={experimentIdFilter}
                  onChange={(e) => setExperimentIdFilter(e.target.value)}
                  placeholder="e.g. salon-pain"
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={loadExperimentFunnel}
                  disabled={experimentLoading}
                >
                  Load Funnel
                </Button>
              </Box>
            </Box>

            {experimentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {experimentError}
              </Alert>
            )}

            {experimentLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!experimentLoading && experimentFunnelData && (
              <Box>
                {experimentFunnelData.variants?.map((variant) => (
                  <Paper key={variant.variant_id} sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      {variant.variant_name} (ID: {variant.variant_id})
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        component="span"
                        sx={{ ml: 1 }}
                      >
                        Conversion Rate: {(variant.conversion_rate * 100).toFixed(2)}%
                      </Typography>
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Stage</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Count
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {variant.stages.map((stage) => (
                            <TableRow key={stage.name}>
                              <TableCell>{stage.name}</TableCell>
                              <TableCell align="right">{stage.count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ))}
              </Box>
            )}
          </TabPanel>

          {/* ─── Raw Events Tab ──────────────────────────────────────── */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Event Name (optional)"
                    value={eventsEventName}
                    onChange={(e) => setEventsEventName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Page (optional)"
                    value={eventsPage}
                    onChange={(e) => setEventsPage(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    onClick={() => loadEvents()}
                    disabled={eventsLoading}
                    fullWidth
                  >
                    Load Events
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => exportEventsAsCSV()}
                    disabled={eventsLoading}
                    fullWidth
                  >
                    Export as CSV
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {eventsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {eventsError}
              </Alert>
            )}

            {eventsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!eventsLoading && eventsData && (
              <Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Referrer</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UTM Source</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Experiment</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Variant</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eventsData.events?.map((event, idx) => (
                        <TableRow key={event.id || idx}>
                          <TableCell>{new Date(event.timestamp).toLocaleString()}</TableCell>
                          <TableCell>{event.event_name}</TableCell>
                          <TableCell>{event.page}</TableCell>
                          <TableCell
                            sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {event.referrer || '-'}
                          </TableCell>
                          <TableCell>{event.utm_source || '-'}</TableCell>
                          <TableCell>{event.experiment_id || '-'}</TableCell>
                          <TableCell>{event.variant_id || '-'}</TableCell>
                          <TableCell>{event.country || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {eventsData.next_cursor && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={() => loadEvents(eventsData.next_cursor ?? undefined)}
                      disabled={eventsLoading}
                    >
                      Load More
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </TabPanel>
        </Paper>
      </Box>
    </Container>
  );
}
